import React, { useState, useEffect, useCallback, useRef } from 'react'
import type { InputProps } from './Input'
import Input from './Input'
import Icon from './Icon'
import { SearchIcon } from 'lucide-react'

export const DEBOUNCE_DELAY = 300

type SearchInputProps = Omit<InputProps, 'value' | 'leftIcon' | 'state' | 'onChange'> & {
    value?: string
    debounceDelay?: number
    onChange?: (value: string) => void
}

const SearchInput = ({ value: propValue, debounceDelay = DEBOUNCE_DELAY, onChange, ...props }: SearchInputProps) => {
    const [localValue, setLocalValue] = useState(propValue || '')
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        // eslint-disable-next-line react/set-state-in-effect -- syncs this state after commit, as it always has; setting it during render would change when the sync runs
        setLocalValue(propValue || '')
    }, [propValue])

    const debouncedOnChange = useCallback(
        (value: string) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            timeoutRef.current = setTimeout(() => {
                if (onChange) {
                    onChange(value)
                }
            }, debounceDelay)
        },
        [onChange, debounceDelay]
    )

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
            setLocalValue(newValue)
            debouncedOnChange(newValue)
        },
        [debouncedOnChange]
    )

    // A single-line <input> strips pasted newlines into spaces, which breaks
    // list filtering. When a multi-line list is pasted, normalise the newlines
    // into commas — the separator the search filter understands.
    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLInputElement>) => {
            const pasted = e.clipboardData.getData('text')
            if (!/[\r\n]/.test(pasted)) return

            e.preventDefault()

            const list = pasted
                .split(/[\r\n]+/)
                .map((token) => token.trim())
                .filter(Boolean)
                .join(',')

            const input = e.currentTarget
            const start = input.selectionStart ?? localValue.length
            const end = input.selectionEnd ?? localValue.length
            const newValue = localValue.slice(0, start) + list + localValue.slice(end)

            setLocalValue(newValue)
            debouncedOnChange(newValue)
        },
        [localValue, debouncedOnChange]
    )

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    return (
        <Input
            value={localValue}
            onChange={handleChange}
            leftIcon={<Icon icon={SearchIcon} strokeWidth={1.2} />}
            {...props}
            onPaste={handlePaste}
        />
    )
}

export default SearchInput
